import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { TicketPercent } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';

const couponSchema = z.object({
  code: z.string().min(1, 'Coupon code is required'),
});

type ICouponForm = z.infer<typeof couponSchema>;

type Props = {
  onSubmit: (values: ICouponForm) => void;
  onCancel: () => void;
};

const BillingCouponForm = ({ onSubmit, onCancel }: Props) => {
  const form = useForm<ICouponForm>({
    resolver: zodResolver(couponSchema),
    defaultValues: {
      code: '',
    },
    mode: 'onChange',
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="code"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Enter your coupon code:</FormLabel>
              <FormControl>
                <div className="relative w-full">
                  <TicketPercent className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input {...field} placeholder="Coupon code" className="pl-8" />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-between gap-2">
          <Button variant="outline" onClick={onCancel} type="button">
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={form.formState.isSubmitting || !form.formState.isValid}
            isLoading={form.formState.isSubmitting}
          >
            Apply
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default BillingCouponForm;
